import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ReactNode } from 'react';

interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  trend?: { value: number; label: string };
  className?: string;
  children?: ReactNode;
}

export function StatsCard({ label, value, icon, trend, className, children }: StatsCardProps) {
  const trendDirection = trend ? (trend.value > 0 ? 'up' : trend.value < 0 ? 'down' : 'flat') : null;

  return (
    <div
      className={clsx(
        'bg-surface-800 border border-surface-700 rounded-lg p-5 transition-all duration-200 hover:border-surface-600 hover:-translate-y-0.5',
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-surface-400 truncate">{label}</p>
          <p className="mt-1 text-2xl font-bold text-surface-50 tracking-tight">{value}</p>
        </div>
        {icon && (
          <div className="flex-shrink-0 p-2 bg-surface-700/50 rounded-lg text-surface-400">{icon}</div>
        )}
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1.5">
          {trendDirection === 'up' && <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />}
          {trendDirection === 'down' && <TrendingDown className="h-3.5 w-3.5 text-red-400" />}
          {trendDirection === 'flat' && <Minus className="h-3.5 w-3.5 text-surface-400" />}
          <span
            className={clsx('text-xs font-medium', {
              'text-emerald-400': trendDirection === 'up',
              'text-red-400': trendDirection === 'down',
              'text-surface-400': trendDirection === 'flat',
            })}
          >
            {trend.value > 0 ? '+' : ''}
            {trend.value}%
          </span>
          <span className="text-xs text-surface-500">{trend.label}</span>
        </div>
      )}
      {children}
    </div>
  );
}
